import React, { useState, useEffect } from "react";
import AccountApiClient from "../../services/AccountApiClient";
import TerminalList from "./TerminalList";
import AddEditAccount from "./AddEditAccount";
import { Account, Terminal } from "../../types";

interface AccountListProps {
  businessId: string;
}

const AccountList: React.FC<AccountListProps> = ({ businessId }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isEditAccountOpen, setIsEditAccountOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [copiedAccountId, setCopiedAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (businessId) {
      fetchAccounts();
    } else {
      setAccounts([]);
      setError("");
    }
  }, [businessId]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const accountData = await AccountApiClient.fetchAccounts(businessId);
      setAccounts(accountData);
    } catch (error: any) {
      console.error("Error fetching accounts:", error);
      setError("Failed to load accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    setIsEditAccountOpen(true);
  };

  const handleAddAccount = () => {
    setSelectedAccount(null); // null indicates we're adding, not editing
    setIsEditAccountOpen(true);
  };

  const handleCloseEditAccount = () => {
    setIsEditAccountOpen(false);
    setSelectedAccount(null);
  };

  const handleSaveAccount = (accountData: Account) => {
    if (accountData.id && accounts.find((acc) => acc.id === accountData.id)) {
      // Editing existing account
      console.log("Saving existing account:", accountData);
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountData.id
            ? { ...accountData, terminals: acc.terminals }
            : acc
        )
      );
    } else {
      // Adding new account
      const newAccount = {
        ...accountData,
        id: Date.now().toString(), // Generate new ID
        businessId: businessId, // Set the current business ID
        terminals: [],
      };
      console.log("Adding new account:", newAccount);
      setAccounts((prev) => [...prev, newAccount]);
    }

    // Refresh the account list to get the latest data from the server
    fetchAccounts();
  };

  const handleRefresh = () => {
    if (businessId) {
      fetchAccounts();
    }
  };

  const copyToClipboard = async (text: string, accountId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAccountId(accountId);
      // Clear the copied state after 2 seconds
      setTimeout(() => setCopiedAccountId(null), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedAccountId(accountId);
      setTimeout(() => setCopiedAccountId(null), 2000);
    }
  };

  if (!businessId) {
    return (
      <div className="account-list-container">
        <div className="account-list-header">
          <h3>💼 Accounts</h3>
        </div>
        <p className="account-list-message">
          Please select a business to view accounts
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="account-list-container">
        <div className="account-list-header">
          <h3>💼 Accounts</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="wide-dash-border-btn"
              onClick={handleRefresh}
              title="Refresh Accounts"
              disabled={loading}
            >
              ↻ Refresh
            </button>
            <button
              className="wide-dash-border-btn"
              onClick={handleAddAccount}
              title="Add Account"
            >
              ➕ Add Account
            </button>
          </div>
        </div>
        <div className="account-list-loading">Loading accounts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-list-container">
        <div className="account-list-header">
          <h3>💼 Accounts</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="wide-dash-border-btn"
              onClick={handleRefresh}
              title="Refresh Accounts"
              disabled={loading}
            >
              ↻ Refresh
            </button>
            <button
              className="wide-dash-border-btn"
              onClick={handleAddAccount}
              title="Add Account"
            >
              ➕ Add Account
            </button>
          </div>
        </div>
        <div className="account-list-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="account-list-container">
      <div className="account-list-header">
        <h3>💼 Accounts ({accounts.length})</h3>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            className="wide-dash-border-btn"
            onClick={handleRefresh}
            title="Refresh Accounts"
            disabled={loading}
          >
            ↻ Refresh
          </button>
          <button
            className="wide-dash-border-btn"
            onClick={handleAddAccount}
            title="Add Account"
          >
            ➕ Add Account
          </button>
        </div>
      </div>
      {accounts.length > 0 && (
        <div className="account-list">
          {accounts.map((account) => (
            <div key={account.id} className="account-item">
              <div className="account-info">
                <div className="account-header">
                  <div className="account-name">
                    {account.identifier}
                    <span className="detail-value account-gateway">
                      PG: {account.paymentGateway || "Not specified"}
                    </span>
                  </div>
                  <div className="account-header-actions">
                    <span
                      className={`account-status ${
                        account.isActive ? "status-active" : "status-inactive"
                      }`}
                    >
                      {account.isActive ? "Active" : "Inactive"}
                    </span>
                    <button
                      className="btn-icon btn-edit-icon"
                      onClick={() => handleEditAccount(account)}
                      title="Edit Account"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
                <div className="account-details">
                  <div className="account-detail-flex">
                    <div
                      className="account-detail-item"
                      style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
                    >
                      <span className="detail-label">Identifier:</span>
                      <span
                        className="detail-value-inline"
                        style={{
                          whiteSpace: "nowrap",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <span
                          className="detail-value account-id"
                          title={account.id || "Not specified"}
                        >
                          {account.id
                            ? account.id.length > 15
                              ? account.id.substring(0, 15) + "..."
                              : account.id
                            : "Not specified"}
                        </span>
                        {account.id && (
                          <button
                            className="btn-icon btn-copy-icon"
                            onClick={() =>
                              copyToClipboard(account.id, account.id)
                            }
                            title={
                              copiedAccountId === account.id
                                ? "Copied!"
                                : "Copy to clipboard"
                            }
                            style={{
                              marginLeft: "0.5rem",
                              fontSize: "0.875rem",
                              background:
                                copiedAccountId === account.id
                                  ? "#10b981"
                                  : "transparent",
                              color:
                                copiedAccountId === account.id
                                  ? "white"
                                  : "#667eea",
                              border: "1px solid",
                              borderColor:
                                copiedAccountId === account.id
                                  ? "#10b981"
                                  : "#e5e7eb",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {copiedAccountId === account.id ? "✓" : "⧉"}
                          </button>
                        )}
                      </span>
                    </div>
                    <div
                      className="account-detail-item"
                      style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
                    >
                      <span className="detail-label">MID/GMPW:</span>
                      <span className="detail-value account-mid">
                        {account.mid || "Not specified"}
                      </span>
                      <span className="detail-value account-gmpw">
                        {account.gmpw || "Not specified"}
                      </span>
                    </div>
                  </div>

                  <div
                    className="account-detail-item"
                    style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
                  >
                    <span className="detail-label">Max Amount:</span>
                    <span className="detail-value account-max-amount">
                      ${account.allowedMaxAmount || 0}
                    </span>
                  </div>
                </div>
                <TerminalList
                  terminals={account.terminals}
                  accountId={account.id}
                  businessId={businessId}
                  onRefresh={fetchAccounts}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {isEditAccountOpen && (
        <AddEditAccount
          isOpen={isEditAccountOpen}
          account={selectedAccount}
          onClose={handleCloseEditAccount}
          onSave={handleSaveAccount}
          businessId={businessId}
        />
      )}
    </div>
  );
};

export default AccountList;
